import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import type { DragOverEvent } from '@dnd-kit/core'
import { createPortal } from 'react-dom'
import { IconArrowsMove, IconChevronDown, IconChevronUp, IconLayoutDashboard, IconPlus, IconSettings2, IconX } from '@tabler/icons-react'
import { useI18n } from '../../lib/i18n'

/**
 * Nada de transformaciones por elemento.
 *
 * La estrategia de dnd-kit calcula desplazamientos suponiendo celdas del mismo
 * tamaño; con anchos de 1 a 4 columnas, esas cuentas no describen la rejilla
 * real y los widgets se cruzaban y encogían al arrastrar. El movimiento lo
 * anima FLIP a partir de la posición que el navegador ya calculó, que no puede
 * equivocarse porque mide en vez de predecir.
 */
const noItemTransforms = () => null

export type DashboardWidgetSpec = {
  id: string
  label: string
  defaultSpan: 1 | 2 | 3 | 4
  /**
   * No sale en un dashboard recién estrenado; se agrega desde «Agregar widget».
   *
   * Es para lo que no tiene nada que enseñar el primer día —analíticas de un
   * sitio sin visitas, un dominio propio que todavía no se ha conectado—: doce
   * tarjetas vacías al abrir por primera vez no enseñan el producto, lo
   * esconden. Sólo afecta a quien no tiene disposición guardada; a nadie se le
   * quita un widget que ya estaba usando.
   */
  defaultHidden?: boolean
  /**
   * Aparece solo cuando hay algo que enseñar: la primera colección, el primer
   * contacto, la primera venta. Hasta entonces espera guardado, en «Agregar
   * widget», como los `defaultHidden`.
   *
   * `false` = todavía nada (o cargando). Al pasar a `true` sale, una vez, y ya
   * se queda aunque luego vuelva a estar vacío. Si el dueño lo quita a mano, no
   * vuelve solo: quitarlo fue su decisión. Sin definir, no espera nada.
   */
  appearsWhen?: boolean
  /**
   * Se queda al final (las notas): lo que va apareciendo entra justo antes.
   * Si el dueño la mueve a mano, manda su sitio.
   */
  stayLast?: boolean
  content: ReactNode
}

/**
 * Coloca `ids` al final del dashboard, justo antes del primero que se queda al
 * final (`stayLast`). Es donde entra lo que aparece solo —el primer servicio,
 * la primera entrada del blog— y lo que se recupera desde «Agregar widget»:
 * antes salían donde se habían declarado, o arriba del todo, y empujaban hacia
 * abajo lo que ya se estaba mirando.
 */
export function placeBeforeLast(order: string[], ids: string[], widgets: DashboardWidgetSpec[]): string[] {
  if (!ids.length) return order
  const rest = order.filter((id) => !ids.includes(id))
  const lastIds = new Set(widgets.filter((widget) => widget.stayLast).map((widget) => widget.id))
  const at = rest.findIndex((id) => lastIds.has(id) && !ids.includes(id))
  return at < 0 ? [...rest, ...ids] : [...rest.slice(0, at), ...ids, ...rest.slice(at)]
}

type StoredLayout = {
  order?: string[]
  spans?: Record<string, number>
  /** Widgets que el dueño quitó. Se guardan por id: quitar no es borrar, y hay
   *  que poder devolverlos desde "Agregar widget". */
  hidden?: string[]
  /** De los guardados, los que esperan a tener contenido (`appearsWhen`) y no
   *  los quitó nadie. Son los únicos que vuelven solos. */
  waiting?: string[]
}

const VALID_SPANS = [1, 2, 3, 4] as const

/**
 * Hueco donde la vista que usa esta rejilla coloca el control de "Agregar
 * widget", junto a su botón de edición.
 *
 * El control pertenece a la rejilla —es la que sabe qué widgets están quitados—
 * pero se dibuja arriba, con el resto de acciones. Un portal deja cada cosa
 * donde corresponde sin subir el estado a la vista.
 */
export const WIDGET_TOOLBAR_SLOT_ID = 'dash-widget-toolbar-slot'

/** A partir de aquí un widget tiene sitio de sobra y su contenido puede
 *  desplegarse en vertical en vez de apretarse en una fila. */
const TALL_WIDGET_HEIGHT = 260
/**
 * Banda muerta de cada umbral.
 *
 * Sin ella el widget entra en bucle: las filas de la rejilla se dimensionan por
 * contenido, así que la altura que mide es en parte la que él mismo provoca.
 * Al cruzar el umbral cambia de disposición, la nueva disposición cambia la
 * altura, ésta vuelve a cruzar el umbral al revés, y la portada se veía crecer
 * y encoger varias veces por segundo.
 *
 * La banda tiene que ser MÁS ANCHA que el salto de alto que causa el cambio, o
 * sólo hace el bucle más lento. Si un cambio de disposición mueve el alto más
 * que eso, el umbral no es el problema: la disposición lo es, y hay que
 * quitarla — como se hizo con la portada a 2/4.
 *
 * Es la misma solución que ya usa el arrastre de anchura unas líneas más abajo
 * —media columna de histéresis— y por el mismo motivo.
 */
const TALL_HYSTERESIS = 28

/** Pointer position feels predictable with mixed-width widgets. Keyboard drags
 *  have no pointer coordinates, so they fall back to geometric proximity. */
const widgetCollisionDetection: CollisionDetection = (args) => {
  const pointerHits = pointerWithin(args)
  return pointerHits.length ? pointerHits : closestCenter(args)
}

function isValidSpan(value: number | undefined): value is 1 | 2 | 3 | 4 {
  return value !== undefined && VALID_SPANS.includes(value as 1 | 2 | 3 | 4)
}

/** `null` cuando este dashboard todavía no tiene disposición guardada. Es lo
 *  que separa a un usuario nuevo de uno que ya colocó sus widgets. */
function readLayout(storageKey: string): StoredLayout | null {
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (raw === null) return null
    const parsed = JSON.parse(raw) as StoredLayout
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

/**
 * Qué widgets arrancan quitados.
 *
 * Los `defaultHidden` sólo se aplican al estrenar. Quien ya tiene disposición
 * guardada manda, aunque nunca haya quitado nada: esconderle un widget que
 * venía usando porque cambió un valor por omisión sería decidir por él.
 */
export function initialHidden(layout: StoredLayout | null, widgets: DashboardWidgetSpec[]): { hidden: string[]; waiting: string[] } {
  const fresh = widgets.filter((widget) => isFresh(layout, widget.id))
  return {
    hidden: [...new Set([...(layout?.hidden ?? []), ...fresh.filter(startsStored).map((widget) => widget.id)])],
    waiting: [...new Set([...(layout?.waiting ?? []), ...fresh.filter(startsWaiting).map((widget) => widget.id)])],
  }
}

/**
 * Un widget que este dashboard nunca ha visto: no hay disposición, o el
 * widget no estaba en ella (es nuevo, como el de Blog). A ésos sí se les
 * aplica cómo nacen; a uno que ya estaba, nunca.
 */
function isFresh(layout: StoredLayout | null, id: string): boolean {
  return !layout || !(layout.order ?? []).includes(id)
}

const startsWaiting = (widget: DashboardWidgetSpec) => !widget.defaultHidden && widget.appearsWhen === false
const startsStored = (widget: DashboardWidgetSpec) => Boolean(widget.defaultHidden) || startsWaiting(widget)

function SortableWidget({ widget, span, layoutSpan, layoutColumns, editing, draggable, mobileOrdering, canMoveUp, canMoveDown, jiggleDelay, onMoveUp, onMoveDown, onSpanChange, onRemove }: {
  widget: DashboardWidgetSpec
  /** La proporción que ocupa, en el vocabulario de cuatro pistas: es lo que
   *  eligen las reglas de contenido, y en tablet no coincide con la pista. */
  span: 1 | 2 | 3 | 4
  /** Columnas visuales en el breakpoint actual: iPad usa una escala 1/2–2/2. */
  layoutSpan: 1 | 2 | 3 | 4
  layoutColumns: 2 | 4
  editing: boolean
  draggable: boolean
  mobileOrdering: boolean
  canMoveUp: boolean
  canMoveDown: boolean
  /** Desfase de la vibración: en fase, doce widgets laten como uno solo. */
  jiggleDelay: number
  onMoveUp: () => void
  onMoveDown: () => void
  onSpanChange: (span: 1 | 2 | 3 | 4) => void
  onRemove: () => void
}) {
  const { t } = useI18n()
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id: widget.id, disabled: !draggable })

  /**
   * Marca el widget cuando le sobra alto.
   *
   * El ancho lo resuelven las container queries, pero el alto no: consultarlo
   * exigiría `container-type: size`, que obliga a la caja a ignorar su
   * contenido — y como el alto de cada fila de la rejilla sale del widget más
   * alto, contenerlo colapsaría las filas. Medirlo es la vía que no rompe el
   * reparto de la rejilla.
   */
  const [isTall, setIsTall] = useState(false)
  const cardRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    const node = cardRef.current
    if (!node) return
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return
      const height = entry.contentRect.height
      /* Ya activo, hace falta bajar de `umbral - banda` para desactivarlo; ya
         inactivo, hace falta alcanzar el umbral entero. Así el estado no puede
         volver atrás a la misma altura a la que avanzó. */
      const settle = (active: boolean, threshold: number, band: number) => (
        active ? height >= threshold - band : height >= threshold
      )
      setIsTall((active) => settle(active, TALL_WIDGET_HEIGHT, TALL_HYSTERESIS))
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  const resizeRef = useRef<{ pointerId: number; startX: number; startSpan: number; columnStep: number; maxColumns: number; direction: 1 | -1 } | null>(null)
  const [isResizing, setIsResizing] = useState(false)

  function handleResizeStart(event: ReactPointerEvent<HTMLButtonElement>, direction: 1 | -1) {
    const grid = event.currentTarget.closest<HTMLElement>('.dash-custom-widget-grid')
    if (!grid) return
    event.preventDefault()
    // El arrastre de la tarjeta vive en el elemento padre: sin esto, tirar de
    // una esquina movería el widget en vez de redimensionarlo.
    event.stopPropagation()
    const styles = window.getComputedStyle(grid)
    const tracks = styles.gridTemplateColumns.split(/\s+/).filter(Boolean)
    const maxColumns = Math.max(1, Math.min(4, tracks.length))
    const gap = Number.parseFloat(styles.columnGap) || 0
    const columnStep = (grid.getBoundingClientRect().width - gap * (maxColumns - 1)) / maxColumns + gap
    resizeRef.current = { pointerId: event.pointerId, startX: event.clientX, startSpan: layoutSpan, columnStep, maxColumns, direction }
    event.currentTarget.setPointerCapture(event.pointerId)
    setIsResizing(true)
  }

  function handleResizeMove(event: ReactPointerEvent<HTMLButtonElement>) {
    const resize = resizeRef.current
    if (!resize || resize.pointerId !== event.pointerId) return
    // Media columna de histéresis: saltar en el punto exacto hacía parpadear el
    // ancho cuando el cursor temblaba sobre el umbral.
    const raw = ((event.clientX - resize.startX) * resize.direction) / resize.columnStep
    const columnDelta = Math.trunc(raw + Math.sign(raw) * 0.5)
    const nextSpan = Math.max(1, Math.min(resize.maxColumns, resize.startSpan + columnDelta)) as 1 | 2 | 3 | 4
    /* En iPad `span` conserva la proporción persistida de desktop (2/4 o
       4/4), mientras `nextSpan` pertenece a la rejilla visual (1/2 o 2/2).
       Compararlos directamente hacía que 1/2 → 2/2 pareciera no cambiar
       porque el 1/2 estaba guardado como 2/4. */
    if (nextSpan !== layoutSpan) onSpanChange(nextSpan)
  }

  function handleResizeEnd(event: ReactPointerEvent<HTMLButtonElement>) {
    if (resizeRef.current?.pointerId !== event.pointerId) return
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
    resizeRef.current = null
    setIsResizing(false)
  }

  return (
    <article
      ref={(node) => { setNodeRef(node); cardRef.current = node }}
      data-widget-id={widget.id}
      className={`dash-custom-widget dash-custom-widget--span-${span}${isDragging ? ' is-dragging' : ''}${isResizing ? ' is-resizing' : ''}${isTall ? ' is-tall' : ''}`}
      style={{ '--dash-widget-span': layoutSpan, '--dash-jiggle-delay': `${jiggleDelay}ms` } as CSSProperties}
      aria-label={t('dash.widgetNamed', { name: widget.label })}
      /* En modo edición la tarjeta entera es el asa. Un tirador de puntitos
         obliga a apuntar a 28 píxeles para mover un bloque de 300. */
      {...(draggable ? { ...attributes, ...listeners, tabIndex: 0 } : {})}
    >
      <div className="dash-widget-content">{widget.content}</div>

      {editing ? (
        <>
          {/* Señal de que la tarjeta entera se agarra: en el centro, al tamaño
              del gesto que representa. Aparece al acercarse, porque en reposo
              doce iconos de mover son ruido. */}
          {draggable ? <span className="dash-widget-move-hint" aria-hidden="true"><IconArrowsMove size={30} /></span> : null}

          {mobileOrdering ? (
            <div className="dash-widget-order-controls" aria-label={t('home.grid.reorder', { label: widget.label })}>
              <button
                type="button"
                aria-label={t('home.grid.moveUpOne', { label: widget.label })}
                title={t('home.grid.moveUp')}
                disabled={!canMoveUp}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => { event.stopPropagation(); onMoveUp() }}
              >
                <IconChevronUp size={18} />
              </button>
              <button
                type="button"
                aria-label={t('home.grid.moveDownOne', { label: widget.label })}
                title={t('home.grid.moveDown')}
                disabled={!canMoveDown}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => { event.stopPropagation(); onMoveDown() }}
              >
                <IconChevronDown size={18} />
              </button>
            </div>
          ) : null}

          {/* Quitar, donde antes estaba la pista de mover. No pide confirmación
              porque no destruye nada: el widget vuelve desde "Agregar". */}
          <button
            type="button"
            className="dash-widget-remove"
            aria-label={t('home.grid.removeOne', { label: widget.label })}
            title={t('home.grid.remove')}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => { event.stopPropagation(); onRemove() }}
          >
            <IconX size={14} />
          </button>

          {!mobileOrdering ? (
            <>
              {/* Un asa por dirección de crecimiento: la derecha ensancha, la
                  izquierda estrecha desde el otro lado. En celular todos los
                  widgets ocupan una columna, así que esos controles no aplican. */}
              <button
                type="button"
                className="dash-widget-corner is-end"
                aria-label={t('home.grid.widen', { label: widget.label })}
                title={t('home.grid.resize')}
                onPointerDown={(event) => handleResizeStart(event, 1)}
                onPointerMove={handleResizeMove}
                onPointerUp={handleResizeEnd}
                onPointerCancel={handleResizeEnd}
              />
              <button
                type="button"
                className="dash-widget-corner is-start"
                aria-label={t('home.grid.narrow', { label: widget.label })}
                title={t('home.grid.resize')}
                onPointerDown={(event) => handleResizeStart(event, -1)}
                onPointerMove={handleResizeMove}
                onPointerUp={handleResizeEnd}
                onPointerCancel={handleResizeEnd}
              />
              <span className="dash-widget-span-badge" aria-hidden="true">{layoutSpan}/{layoutColumns}</span>
            </>
          ) : null}
        </>
      ) : null}
    </article>
  )
}

export function CustomizableDashboardGrid({ storageKey, widgets, editing, onEdit }: {
  storageKey: string
  widgets: DashboardWidgetSpec[]
  editing: boolean
  /**
   * Entrar a editar desde dentro de la rejilla.
   *
   * Sólo lo usa el aviso de «no queda ninguno»: el botón de editar vive en la
   * cabecera, y quien acaba de dejar el inicio vacío está mirando el hueco, no
   * la esquina de arriba. Opcional porque la rejilla funciona sin él —el camino
   * de la cabecera sigue ahí— y así no obliga a cablearlo a quien la reutilice.
   */
  onEdit?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const initialLayout = useMemo(() => readLayout(storageKey), [storageKey])
  const [order, setOrder] = useState<string[]>(() => initialLayout?.order ?? [
    ...widgets.filter((widget) => !widget.stayLast).map((widget) => widget.id),
    ...widgets.filter((widget) => widget.stayLast).map((widget) => widget.id),
  ])
  const [spans, setSpans] = useState<Record<string, number>>(() => initialLayout?.spans ?? {})
  const [initialState] = useState(() => initialHidden(initialLayout, widgets))
  const [hidden, setHidden] = useState<string[]>(initialState.hidden)
  const [waiting, setWaiting] = useState<string[]>(initialState.waiting)
  /* Los que ya se trataron como nuevos: los que había al abrir y los que
     llegan después (Depósitos aparece cuando carga el saldo). */
  const seenRef = useRef(new Set<string>([...(initialLayout?.order ?? []), ...widgets.map((widget) => widget.id)]))
  const [addOpen, setAddOpen] = useState(false)
  const [addSource, setAddSource] = useState<'toolbar' | 'placeholder'>('toolbar')
  const placeholderRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (!editing) setAddOpen(false)
  }, [editing])
  useEffect(() => {
    if (!addOpen) return
    const outside = (event: PointerEvent) => {
      if (!(event.target as Element).closest('.dash-widget-add-wrap, .dash-widget-add-placeholder')) setAddOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setAddOpen(false)
        if (addSource === 'placeholder') placeholderRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', outside)
    window.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('pointerdown', outside)
      window.removeEventListener('keydown', escape)
    }
  }, [addOpen, addSource])
  const [mobileOrdering, setMobileOrdering] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches)
  const [tabletSizing, setTabletSizing] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 641px) and (max-width: 1199px)').matches)
  useEffect(() => {
    const media = window.matchMedia('(max-width: 640px)')
    const update = () => setMobileOrdering(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])
  useEffect(() => {
    const media = window.matchMedia('(min-width: 641px) and (max-width: 1199px)')
    const update = () => setTabletSizing(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])
  // El hueco lo dibuja la vista, así que sólo existe después del primer render.
  const [toolbarSlot, setToolbarSlot] = useState<HTMLElement | null>(null)
  useEffect(() => { setToolbarSlot(document.getElementById(WIDGET_TOOLBAR_SLOT_ID)) }, [editing])
  const [activeDrag, setActiveDrag] = useState<{ id: string; width: number; height: number } | null>(null)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const gridRef = useRef<HTMLElement>(null)

  /**
   * Posiciones tomadas justo antes de un cambio del usuario, para animarlo.
   *
   * Se llena a mano y sólo al reordenar o redimensionar. Medir en cada render
   * hacía que FLIP animara también los reflows del contenido — al cargar la
   * página llegan analíticas, mensajes y contactos, cada widget cambia de alto,
   * y el dashboard entero parecía sacudirse solo.
   */
  const pendingFlip = useRef<Map<string, DOMRect> | null>(null)
  /** Guardas contra el bucle de reordenado; ver `handleDragOver`. */
  const previousOrderKey = useRef('')
  const lastReorderAt = useRef(0)
  /** Al usar las flechas en celular, la vista acompaña al widget hasta su nueva
   * posición para que el reordenado no ocurra fuera de cámara. */
  const pendingFollowWidget = useRef<string | null>(null)

  function captureRects() {
    const grid = gridRef.current
    if (!grid) return
    const rects = new Map<string, DOMRect>()
    grid.querySelectorAll<HTMLElement>('[data-widget-id]').forEach((node) => {
      if (node.dataset.widgetId) rects.set(node.dataset.widgetId, node.getBoundingClientRect())
    })
    pendingFlip.current = rects
  }

  /**
   * FLIP: anima desde la posición medida antes del cambio hasta la nueva.
   *
   * Una rejilla CSS no transiciona posiciones ni columnas — un widget que pasa
   * de la fila 2 a la 1 simplemente aparece allí. Animar la diferencia medida
   * vale igual para reordenar que para redimensionar, y no puede
   * desincronizarse del layout porque lo lee en vez de predecirlo.
   */
  useLayoutEffect(() => {
    const first = pendingFlip.current
    pendingFlip.current = null
    const grid = gridRef.current
    if (!first || !grid) return

    /*
     * La pantalla se mueve AQUÍ, antes de animar nada.
     *
     * En este punto el widget ya está en su sitio nuevo y todavía no tiene
     * encima la transformación del FLIP, así que su caja es la definitiva: se
     * puede pedir el desplazamiento sin esperar a nada. Esperar a que la
     * animación terminara —que es lo que hacía antes— partía el gesto en dos
     * tiempos: primero saltaba la tarjeta y un cuarto de segundo después
     * arrancaba la pantalla.
     *
     * Y no descoloca la animación: el FLIP desplaza con `translate3d`, que es
     * relativo a la propia caja del elemento, así que el documento puede
     * desplazarse por debajo mientras dura sin que el recorrido se desvíe.
     */
    const followId = pendingFollowWidget.current
    pendingFollowWidget.current = null
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (followId) {
      grid
        .querySelector<HTMLElement>(`[data-widget-id="${CSS.escape(followId)}"]`)
        ?.scrollIntoView({
          /* `nearest` y no `center`: centrar un widget más alto que la pantalla
             se lleva fuera de vista su cabecera, que es donde están las flechas
             que acabas de pulsar. */
          block: 'nearest',
          inline: 'nearest',
          behavior: reduced ? 'auto' : 'smooth',
        })
    }

    if (reduced) return

    grid.querySelectorAll<HTMLElement>('[data-widget-id]').forEach((node) => {
      const id = node.dataset.widgetId
      const previous = id ? first.get(id) : undefined
      if (!previous) return
      const next = node.getBoundingClientRect()
      const dx = previous.left - next.left
      const dy = previous.top - next.top
      const scaleX = next.width > 0 ? previous.width / next.width : 1
      // Por debajo de un píxel no hay nada que enseñar.
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1 && Math.abs(scaleX - 1) < 0.01) return

      node.animate(
        [
          { transform: `translate3d(${dx}px, ${dy}px, 0) scaleX(${scaleX})`, transformOrigin: 'left top' },
          { transform: 'translate3d(0, 0, 0) scaleX(1)', transformOrigin: 'left top' },
        ],
        /* Deceleración: sale rápido y llega frenando, que es lo que hace que un
           reacomodo se lea como movimiento y no como un salto. Los 320 son para
           que acompañe al desplazamiento suave del navegador, que es más lento
           que los 260 de antes: con la tarjeta ya quieta y la pantalla todavía
           andando, el gesto volvía a partirse en dos. */
        { duration: 320, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
      )
    })
  }, [order, spans])

  /* El seguimiento vive dentro del efecto de arriba: necesita medir el widget
     en el único instante en que está colocado y sin transformar. */

  /* Memorizada porque de ella cuelga el efecto que guarda el layout: como
     array nuevo en cada dibujado, ese efecto se disparaba en TODOS —incluidos
     los cincuenta que provoca un arrastre— y serializaba el layout entero a
     `localStorage` en cada uno. */
  const orderedWidgets = useMemo(() => {
    const widgetIds = widgets.map((widget) => widget.id)
    /* Los que este dashboard no conocía entran al final, antes de las notas. */
    return placeBeforeLast(order.filter((id) => widgetIds.includes(id)), widgetIds.filter((id) => !order.includes(id)), widgets)
      /* Ni los que acaban de llegar y nacen guardados: el efecto que los
         guarda corre después de pintar, y sin esto se asomaban un instante. */
      .filter((id) => !hidden.includes(id) && !(widgets.some((widget) => widget.id === id && !seenRef.current.has(id) && startsStored(widget))))
      .map((id) => widgets.find((widget) => widget.id === id))
      .filter((widget): widget is DashboardWidgetSpec => Boolean(widget))
  }, [widgets, order, hidden])

  /**
   * Los anchos, sin huecos al final de una fila.
   *
   * Qué widgets salen depende del proyecto —sin Stripe no hay Depósitos, sin
   * permiso no hay Contactos—, así que una rejilla de medios anchos quedaba
   * muchas veces con un número impar y media fila vacía al final. Aquí se
   * recorre la rejilla como la pinta el navegador (en orden, fila a fila) y,
   * cuando una fila no se llena, la última tarjeta se estira hasta el borde.
   *
   * Sólo las de tamaño por defecto: un ancho que alguien eligió a mano se
   * respeta aunque deje hueco — es su decisión, no un descuido de la rejilla.
   */
  const filledSpans = useMemo(() => {
    const columns = tabletSizing ? 2 : 4
    const toUnits = (span: number) => (tabletSizing ? (span >= 3 ? 2 : 1) : span)
    const fromUnits = (units: number) => (tabletSizing ? (units === 2 ? 4 : 2) : units) as 1 | 2 | 3 | 4
    const result: (1 | 2 | 3 | 4)[] = []
    let rowUsed = 0
    let rowLast = -1
    const closeRow = () => {
      const gap = columns - rowUsed
      const last = orderedWidgets[rowLast]
      if (gap > 0 && last && !isValidSpan(spans[last.id])) {
        result[rowLast] = fromUnits(toUnits(result[rowLast] ?? last.defaultSpan) + gap)
      }
      rowUsed = 0
      rowLast = -1
    }
    orderedWidgets.forEach((widget, index) => {
      const stored = spans[widget.id]
      const span = isValidSpan(stored) ? stored : widget.defaultSpan
      const units = Math.min(columns, toUnits(span))
      if (rowUsed + units > columns) closeRow()
      result[index] = span
      rowUsed += units
      rowLast = index
      if (rowUsed === columns) closeRow()
    })
    if (rowUsed > 0) closeRow()
    return result
  }, [orderedWidgets, spans, tabletSizing])

  /* Los que llegan con la página ya abierta nacen como los demás nuevos; los
     que esperaban y ya tienen contenido, salen. */
  useEffect(() => {
    const arrived = widgets.filter((widget) => !seenRef.current.has(widget.id))
    arrived.forEach((widget) => seenRef.current.add(widget.id))
    const toStore = arrived.filter(startsStored).map((widget) => widget.id)
    const toWait = arrived.filter(startsWaiting).map((widget) => widget.id)
    const ready = widgets
      .filter((widget) => widget.appearsWhen === true && (waiting.includes(widget.id) || toWait.includes(widget.id)))
      .map((widget) => widget.id)
    if (!toStore.length && !toWait.length && !ready.length) return
    if (ready.length) {
      captureRects()
      /* Lo que aparece porque ya tiene contenido entra al final, no arriba. */
      setOrder((current) => placeBeforeLast(current, ready, widgets))
    }
    setHidden((current) => [...new Set([...current, ...toStore])].filter((id) => !ready.includes(id)))
    setWaiting((current) => [...new Set([...current, ...toWait])].filter((id) => !ready.includes(id)))
  }, [widgets, waiting])

  /** Los quitados, en el orden en que se declararon: así "Agregar" siempre los
   *  lista igual, sin depender de cuándo se quitó cada uno. */
  const hiddenWidgets = widgets.filter((widget) => hidden.includes(widget.id))

  useEffect(() => {
    try {
      // El orden guardado incluye los ocultos: si no, volverían al final al
      // restaurarlos y perderían el sitio que tenían.
      const visibleOrder = orderedWidgets.map((widget) => widget.id)
      window.localStorage.setItem(storageKey, JSON.stringify({
        /* Los guardados también: su id en el orden es lo que dice que este
           dashboard ya los conoce (`isFresh`). */
        order: [...new Set([...order.filter((id) => hidden.includes(id)), ...hidden, ...visibleOrder])],
        spans,
        hidden,
        waiting,
      }))
    } catch {
      // The dashboard remains usable when storage is disabled.
    }
  }, [order, orderedWidgets, spans, hidden, waiting, storageKey])

  function handleDragStart(event: DragStartEvent) {
    const rect = event.active.rect.current.initial
    setActiveDrag({
      id: String(event.active.id),
      width: rect?.width ?? 280,
      height: rect?.height ?? 180,
    })
  }

  /**
   * Reordena mientras se arrastra, no al soltar.
   *
   * Con el reordenado en el drop, la rejilla se quedaba quieta bajo el cursor y
   * daba un salto al final: nunca veías dónde iba a caer el widget hasta que ya
   * había caído. Moviendo el array en `dragOver`, el hueco se abre bajo el
   * puntero y soltar sólo confirma lo que ya está en pantalla.
   */
  /**
   * Reordena mientras se arrastra, no al soltar.
   *
   * Con el reordenado en el drop, la rejilla se quedaba quieta bajo el cursor y
   * daba un salto al final. Moviendo el array aquí, el hueco se abre bajo el
   * puntero y soltar sólo confirma lo que ya se ve.
   *
   * El precio es un bucle: reordenar mueve lo que hay debajo del puntero, lo
   * que dispara otro `dragOver` que lo deshace. En diagonal el puntero alterna
   * entre dos objetivos y el ciclo se realimenta hasta que React aborta y la
   * página queda en blanco. Las dos guardas de abajo son lo que lo corta.
   */
  function handleDragOver(event: DragOverEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const ids = orderedWidgets.map((widget) => widget.id)
    const from = ids.indexOf(String(active.id))
    const to = ids.indexOf(String(over.id))
    if (from < 0 || to < 0) return

    const next = arrayMove(ids, from, to)
    const nextKey = next.join('|')
    // 1. Nunca volver al orden inmediatamente anterior: ese es exactamente el
    //    movimiento de vuelta del ping-pong.
    if (nextKey === previousOrderKey.current || nextKey === ids.join('|')) return
    // 2. Un reacomodo por gesto, no por evento: sin esto, un temblor sobre la
    //    frontera entre dos widgets dispara decenas por segundo.
    if (Date.now() - lastReorderAt.current < 160) return

    previousOrderKey.current = ids.join('|')
    lastReorderAt.current = Date.now()
    captureRects()
    setOrder(next)
  }

  function handleDragEnd() {
    // El orden ya es el correcto: `dragOver` lo fue aplicando por el camino.
    setActiveDrag(null)
    previousOrderKey.current = ''
  }

  function moveWidget(id: string, direction: -1 | 1) {
    const ids = orderedWidgets.map((widget) => widget.id)
    const from = ids.indexOf(id)
    const to = from + direction
    if (from < 0 || to < 0 || to >= ids.length) return
    captureRects()
    pendingFollowWidget.current = id
    setOrder(arrayMove(ids, from, to))
  }

  const widgetPicker = hiddenWidgets.length > 0 ? (
    <div className="dash-widget-add-menu" role="menu">
      {hiddenWidgets.map((widget) => (
        <button
          key={widget.id}
          type="button"
          role="menuitem"
          onClick={() => {
            captureRects()
            setHidden((current) => current.filter((id) => id !== widget.id))
            setOrder((current) => addSource === 'placeholder'
              ? [...current.filter((id) => id !== widget.id), widget.id]
              : placeBeforeLast(current, [widget.id], widgets))
            if (addSource === 'placeholder') setSpans((current) => ({ ...current, [widget.id]: 2 }))
            setWaiting((current) => current.filter((id) => id !== widget.id))
            setAddOpen(false)
            if (addSource === 'placeholder') requestAnimationFrame(() => placeholderRef.current?.focus())
          }}
        >
          <IconPlus size={13} /> {widget.label}
        </button>
      ))}
    </div>
  ) : null

  const activeWidget = activeDrag ? orderedWidgets.find((widget) => widget.id === activeDrag.id) : null

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={widgetCollisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragCancel={() => { setActiveDrag(null); previousOrderKey.current = '' }}
      onDragEnd={handleDragEnd}
    >
      {editing && toolbarSlot ? createPortal(
        <div className="dash-widget-toolbar">
          {/* Lo que dice la cápsula, como «Cambios sin guardar» en la de guardar. */}
          <span className="dash-topbar-pending-label">
            <span className="dash-topbar-pending-dot" aria-hidden="true" />
            {t('widgets.editingLabel')}
          </span>
          <div className="dash-widget-add-wrap">
            <button
              type="button"
              className="dash-secondary-btn dash-widget-add"
              aria-expanded={addOpen && addSource === 'toolbar'}
              disabled={hiddenWidgets.length === 0}
              onClick={() => { setAddSource('toolbar'); setAddOpen((open) => addSource !== 'toolbar' || !open) }}
            >
              <IconPlus size={14} />
              {hiddenWidgets.length === 0 ? t('widgets.allPlaced') : t('widgets.add', { n: hiddenWidgets.length })}
            </button>
            {addOpen && addSource === 'toolbar' ? widgetPicker : null}
          </div>
        </div>,
        toolbarSlot,
      ) : null}

      <SortableContext items={orderedWidgets.map((widget) => widget.id)} strategy={noItemTransforms}>
        <section
          ref={gridRef}
          className={`dash-custom-widget-grid${editing ? ' is-editing' : ''}${editing && mobileOrdering ? ' is-mobile-ordering' : ''}${activeDrag ? ' is-sorting' : ''}`}
          aria-label={t('home.grid.aria')}
        >
          {/* Quitar el último widget dejaba la página en el saludo y un hueco
              en blanco: nada que leer y ninguna pista de que aquello se puede
              deshacer. El aviso ocupa la rejilla entera y lleva la salida
              encima —editar si se está mirando, devolverlos todos si ya se está
              editando—, porque el botón de la cabecera queda lejos de donde
              está la vista de quien acaba de vaciar esto. */}
          {orderedWidgets.length === 0 ? (
            <div className="dash-placeholder dash-widgets-empty">
              <IconLayoutDashboard size={34} aria-hidden="true" />
              <strong>{t('home.grid.emptyTitle')}</strong>
              <span>{t('home.grid.emptyBody')}</span>
              {editing ? (
                <button type="button" className="dash-secondary-btn" onClick={() => { captureRects(); setHidden([]); setWaiting([]) }}>
                  {t('home.grid.restore')}
                </button>
              ) : onEdit ? (
                <button type="button" className="dash-secondary-btn" onClick={onEdit}>
                  <IconSettings2 size={14} /> {t('home.grid.edit')}
                </button>
              ) : null}
            </div>
          ) : null}
          {orderedWidgets.map((widget, index) => {
            const span = filledSpans[index] ?? widget.defaultSpan
            /* Tablet ofrece sólo media anchura o anchura completa. Guardamos
               esos tamaños como 2/4 y 4/4 para que el mismo layout conserve
               una proporción equivalente al volver a desktop. */
            const layoutSpan = tabletSizing ? (span >= 3 ? 2 : 1) : span
            /* La clase dice qué PROPORCIÓN de la fila ocupa la tarjeta, y de
               ella cuelgan las reglas de contenido que no pueden preguntar por
               el ancho —listas a dos columnas en 4/4, filas de una línea en 2/4
               y 3/4—. En tablet hay que traducirla al vocabulario de cuatro
               pistas: sin esto, un widget guardado como 1/4 se dibujaba a media
               fila —350 puntos— con la receta del cuarto de ancho, y uno de 3/4
               ocupaba la fila entera con la de tres cuartos. De ahí que en iPad
               se viera todo descuadrado sin haber tocado nada. */
            const proportionSpan = tabletSizing ? (layoutSpan === 2 ? 4 : 2) : span
            return (
              <SortableWidget
                key={widget.id}
                widget={widget}
                span={proportionSpan}
                layoutSpan={layoutSpan}
                layoutColumns={tabletSizing ? 2 : 4}
                editing={editing}
                draggable={editing && !mobileOrdering}
                mobileOrdering={editing && mobileOrdering}
                canMoveUp={index > 0}
                canMoveDown={index < orderedWidgets.length - 1}
                jiggleDelay={(index % 5) * 90}
                onMoveUp={() => moveWidget(widget.id, -1)}
                onMoveDown={() => moveWidget(widget.id, 1)}
                onSpanChange={(nextSpan) => {
                  /* Sin FLIP: esto se llama a cada paso del arrastre, y cada
                     llamada relanzaba una animación de 260ms sobre TODOS los
                     widgets. Con el dedo moviéndose, ninguna llegaba a
                     terminar: la rejilla iba permanentemente a medio camino de
                     una posición que ya había cambiado, y las tarjetas se veían
                     ir por detrás del dedo y salirse de su sitio. Un arrastre ya
                     trae su propio movimiento —el del dedo—; lo que FLIP anima
                     son los cambios de golpe: reordenar, quitar y devolver. */
                  const storedNextSpan = tabletSizing ? (nextSpan === 1 ? 2 : 4) : nextSpan
                  setSpans((current) => ({ ...current, [widget.id]: storedNextSpan }))
                }}
                onRemove={() => {
                  captureRects()
                  setHidden((current) => [...current, widget.id])
                  // Quitado a mano: ya no vuelve solo aunque llegue contenido.
                  setWaiting((current) => current.filter((id) => id !== widget.id))
                }}
              />
            )
          })}
          {editing && hiddenWidgets.length > 0 ? (
            <div className="dash-widget-add-placeholder" style={{ gridColumn: `span ${mobileOrdering || tabletSizing ? 1 : 2}` }}>
              <button ref={placeholderRef} type="button" className="dash-widget-add-card" aria-haspopup="menu" aria-expanded={addOpen && addSource === 'placeholder'} onClick={() => {
                setAddSource('placeholder')
                setAddOpen((open) => addSource !== 'placeholder' || !open)
              }}>
                <span className="dash-widget-add-card-icon" aria-hidden="true"><IconPlus size={22} stroke={1.6} /></span>
                <span>{t('widgets.addPlaceholder')}</span>
              </button>
              {addOpen && addSource === 'placeholder' ? widgetPicker : null}
            </div>
          ) : null}
        </section>
      </SortableContext>
      <DragOverlay adjustScale={false} dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.2, 0, 0, 1)' }}>
        {/* La copia lleva también `dash-custom-widget`: fuera de la rejilla el
            elemento dejaba de ser contenedor de consulta y de casar con los
            selectores `.dash-custom-widget .x`, así que el contenido colapsaba a
            su forma más estrecha justo al levantarlo. */}
        {activeWidget && activeDrag ? (
          <div
            className="dash-custom-widget dash-widget-drag-overlay"
            style={{ width: activeDrag.width, height: activeDrag.height }}
            aria-hidden="true"
          >
            <div className="dash-widget-content">{activeWidget.content}</div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
